import type {i18n} from 'i18next';
import {getFileNames} from './plugins/getFileNames';

function registerLanguageTranslations(
  i18nInstance: i18n,
  language: string,
  namespaces: string[],
  modules: Record<string, unknown>,
) {
  for (const namespace of namespaces) {
    try {
      const matchingKey = Object.keys(modules).find(key =>
        key.includes(`/locales/${language}/${namespace}.json`),
      );

      if (matchingKey) {
        const module = modules[matchingKey] as
          | Record<string, unknown>
          | {default: Record<string, unknown>};
        const translations = 'default' in module ? module.default : module;

        if (translations) {
          i18nInstance.addResourceBundle(
            language,
            namespace,
            translations,
            true, // deep merge
            false, // don't overwrite - let lazy loaded translations take precedence
          );
        }
      }
    } catch (error) {
      console.warn(
        `Failed to register ${language}/${namespace} shared translations:`,
        error,
      );
    }
  }
}

export function registerSharedTranslations(i18nInstance: i18n) {
  const {namespaces, modules} = getFileNames();

  // Register for current language and fallback
  const currentLanguage = i18nInstance.language?.slice(0, 2) || 'en';
  const fallbackLng = i18nInstance.options.fallbackLng;
  const fallbackLanguage = Array.isArray(fallbackLng)
    ? fallbackLng[0]
    : typeof fallbackLng === 'string'
      ? fallbackLng
      : 'en';

  registerLanguageTranslations(i18nInstance, currentLanguage, namespaces, modules);
  if (fallbackLanguage !== currentLanguage) {
    registerLanguageTranslations(i18nInstance, fallbackLanguage, namespaces, modules);
  }

  // Register translations when language changes
  i18nInstance.on('languageChanged', (newLanguage: string) => {
    const lang = newLanguage.slice(0, 2);
    registerLanguageTranslations(i18nInstance, lang, namespaces, modules);
  });
}

export type {i18n};
